// src/lib/planFeatures.ts

import type { UserProfile } from '@/types';

export type PlanType = 'Free' | 'Standard';

export interface PlanFeatures {
  // 広告
  showAds: boolean;
  
  // エクスポート
  csvExport: boolean;
  csvFullColumns: boolean;
  pdfExport: boolean;
  
  // 連携
  googleCalendarSync: boolean;
  
  // 🆕 制限
  maxActiveProjects: number | 'unlimited';
  maxActiveClients: number | 'unlimited';
  maxContactsPerClient: number | 'unlimited';
}

export const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  Free: {
    showAds: true,
    csvExport: true,
    csvFullColumns: false,
    pdfExport: false,
    googleCalendarSync: false,
    maxActiveProjects: 5,
    maxActiveClients: 5,
    maxContactsPerClient: 2,
  },
  Standard: {
    showAds: false,
    csvExport: true,
    csvFullColumns: true,
    pdfExport: true,
    googleCalendarSync: true,
    maxActiveProjects: 'unlimited',
    maxActiveClients: 'unlimited',
    maxContactsPerClient: 'unlimited',
  },
};

export const getPlanFeatures = (user: UserProfile | null): PlanFeatures => {
  const plan = user?.plan || 'Free';
  return PLAN_FEATURES[plan] || PLAN_FEATURES.Free;
};

export const canUseFeature = (
  user: UserProfile | null,
  feature: keyof PlanFeatures
): boolean => {
  const features = getPlanFeatures(user);
  const value = features[feature];
  
  if (typeof value === 'boolean') {
    return value;
  }
  return true;
};

/**
 * 🆕 制限数を超えているかチェック
 */
export const isOverLimit = (
  user: UserProfile | null,
  feature: 'maxActiveProjects' | 'maxActiveClients' | 'maxContactsPerClient',
  currentCount: number
): boolean => {
  const features = getPlanFeatures(user);
  const limit = features[feature];
  
  if (limit === 'unlimited') {
    return false;
  }
  
  return currentCount >= limit;
};

/**
 * 🆕 残り作成可能数を取得
 */
export const getRemainingCount = (
  user: UserProfile | null,
  feature: 'maxActiveProjects' | 'maxActiveClients' | 'maxContactsPerClient',
  currentCount: number
): number | 'unlimited' => {
  const features = getPlanFeatures(user);
  const limit = features[feature];
  
  if (limit === 'unlimited') {
    return 'unlimited';
  }
  
  return Math.max(0, limit - currentCount);
};

export const FREE_CSV_COLUMNS = ['date', 'project', 'startTime', 'endTime'];

export const ALL_CSV_COLUMNS = [
  'date',
  'project', 
  'startTime',
  'endTime',
  'actualHours',
  'performance',
  'category',
  'memo',
];

export const getPlanDisplayName = (plan: PlanType): string => {
  switch (plan) {
    case 'Standard':
      return 'Standardプラン';
    case 'Free':
    default:
      return 'Freeプラン';
  }
};

export const STANDARD_PLAN_BENEFITS = [
  '広告非表示',
  'Googleカレンダー連携',
  'PDFエクスポート',
  'CSV全項目エクスポート',
  'プロジェクト無制限',
  'クライアント無制限',
  '担当者登録無制限',
];
